import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, QrCode, Link2, Award, ArrowRight, AlertTriangle, Check, Building2, User, Store, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';

const VERIFACTU_IMG = 'https://static.prod-images.emergentagent.com/jobs/81285e37-432c-430a-b239-6fe7ab6d07e8/images/f5027d4464895842fba582b6d4a7ecf3d86fa5e47955f4427e60fc6df8e149fc.png';

const VeriFactuSection = () => {
  const { t } = useLanguage();

  const mandatory = [
    { icon: Building2, text: t('vf_who_1') || 'Companies (SL, SA)' },
    { icon: User, text: t('vf_who_2') || 'Freelancers (Autonomos)' },
    { icon: Store, text: t('vf_who_3') || 'Shops, Restaurants & Bars' },
    { icon: ShoppingBag, text: t('vf_who_4') || 'Retail & All Businesses' },
  ];

  const requires = [
    t('vf_req_1') || 'Invoice integrity - No deletion or manipulation',
    t('vf_req_2') || 'Traceability - Complete audit trail',
    t('vf_req_3') || 'QR codes on all invoices for verification',
    t('vf_req_4') || 'Automatic reporting to AEAT (Tax Authority)',
  ];

  const ready = [
    { label: t('vf_ready_1') || '100% VeriFactu Compliant', bold: true },
    { label: t('vf_ready_2') || 'Cryptographic Hash Chain - Every invoice cryptographically linked' },
    { label: t('vf_ready_3') || 'Automatic QR Codes on every receipt' },
    { label: t('vf_ready_4') || 'AEAT Integration - Automatic submission to Tax Authority' },
    { label: t('vf_ready_5') || 'Spanish IVA Support: 21%, 10%, 4%, 0%' },
  ];

  return (
    <section
      className="py-24 md:py-32 bg-[#F8FAFC] relative overflow-hidden"
      data-testid="verifactu-section"
    >
      {/* Decorative Lines */}
      <div className="absolute inset-0 opacity-[0.03]">
        {[...Array(15)].map((_, i) => (
          <div key={i} className="absolute w-full h-px bg-[#1e3a5f]" style={{ top: `${i * 7}%` }} />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#22c55e]/10 border-2 border-[#22c55e]/20 text-[#22c55e] text-sm font-bold">
            <ShieldCheck className="w-4 h-4" />
            {t('verifactu_label') || 'AEAT REGULATION'}
          </span>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] tracking-tight mb-4">
            {t('vf_section_title') || 'VeriFactu Ready'}
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            {t('vf_section_subtitle') || 'VendFactura is fully prepared for Spain\'s new anti-fraud invoicing requirements. Your business stays compliant automatically.'}
          </p>
        </motion.div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-16">
          {/* Left: What's Required */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white border-2 border-slate-200 p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-[#1e3a5f]">
                {t('vf_mandatory_title') || 'VeriFactu is Mandatory'}
              </h3>
            </div>

            <p className="text-sm text-slate-600 mb-6">
              {t('vf_mandatory_desc') || 'Spain\'s anti-fraud regulation requires ALL businesses using invoicing software to comply:'}
            </p>

            {/* Who must comply */}
            <div className="space-y-3 mb-8">
              {mandatory.map((m, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-50 flex items-center justify-center flex-shrink-0">
                    <m.icon className="w-4 h-4 text-red-500" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">{m.text}</span>
                </div>
              ))}
            </div>

            {/* What it requires */}
            <h4 className="text-sm font-bold text-[#1e3a5f] mb-4 uppercase tracking-wide">
              {t('vf_what_requires') || 'What VeriFactu Requires:'}
            </h4>
            <div className="space-y-3">
              {requires.map((r, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-[#1e3a5f] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-sm text-slate-600">{r}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: VendFactura is Ready */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-[#1e3a5f] border-2 border-[#22c55e]/30 p-8 text-white"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#22c55e] flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">
                {t('vf_ready_title') || 'VendFactura is Ready'}
              </h3>
            </div>

            <div className="space-y-4 mb-8">
              {ready.map((r, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-[#22c55e] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className={`text-sm ${r.bold ? 'text-[#22c55e] font-bold text-base' : 'text-slate-300'}`}>
                    {r.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Visual */}
            <div className="mt-6 border-2 border-white/10 overflow-hidden">
              <img
                src={VERIFACTU_IMG}
                alt="VeriFactu Compliance"
                className="w-full h-auto object-cover"
                data-testid="verifactu-image"
              />
            </div>

            <Link
              to="/register"
              className="mt-8 inline-flex items-center gap-2 bg-[#22c55e] text-white px-6 py-3 font-bold text-sm hover:bg-[#16a34a] transition-colors"
              data-testid="verifactu-cta"
            >
              {t('verifactu_cta') || 'Get Compliant Today'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VeriFactuSection;
